import React from "react";
import styled from "styled-components";

// #region region styles
const RadioButtonStyles = styled.div`
    .main-radio {
        width: 16px;
        height: 16px;
    }

    .disabled {
        color: #4f5b6d;
        cursor: not-allowed;
    }
`;
// #endregion

/** RadioButton component renders a customizable radio button.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className=""] - Additional class names for the radio button.
 * @param {Object} [props.style] - Inline styles for the radio button.
 * @param {boolean} [props.isSelected=false] - Determines if the radio button is selected.
 * @param {Function} [props.onClick=() => {}] - Callback function to handle click events.
 * @param {boolean} [props.disabled=false] - Determines if the radio button is disabled.
 * @param {string} [props.radioColor="var(--App-Accent-Outline)"] - The color of the radio button.
 *
 * @returns {JSX.Element} The rendered RadioButton component.
 */
export default React.forwardRef((props, ref) => {
    const { className = "", isSelected = false, onClick = () => {}, style = {}, disabled = false } = props;

    const getRadioImage = () => {
        if (disabled) {
            if (!isSelected) return <RadioDisabled className={"main-radio cursor-not-allowed"} ref={ref} />;
            else return <RadioEnabled className={"main-radio cursor-not-allowed disabled"} ref={ref} />;
        }
        if (isSelected) return <RadioEnabled className={"main-radio cursor-pointer"} ref={ref} />;
        return <RadioNormal className={"main-radio cursor-pointer"} ref={ref} />;
    };

    // #region render
    return (
        <RadioButtonStyles
            style={{
                color: props?.radioColor || "var(--App-Accent-Outline)",
                height: "16px",
                ...style
            }}
            className={`radio-standard ${className} ${disabled ? "disabled" : ""}`}
            onClick={disabled ? null : onClick}>
            {getRadioImage()}
        </RadioButtonStyles>
    );
    // #endregion
});

// #region SMALL COMPONENTS
const RadioNormal = React.forwardRef(({ className }, ref) => (
    <svg
        className={className}
        ref={ref}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="#10141B"
        xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7" stroke="var(--text-icons-gray-200)" strokeWidth="1" />
    </svg>
));

const RadioEnabled = React.forwardRef(({ className }, ref) => (
    <svg
        className={className}
        ref={ref}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="#10141B"
        xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" />
        <circle cx="8" cy="8" r="4" fill="currentColor" />
    </svg>
));

const RadioDisabled = React.forwardRef(({ className }, ref) => (
    <svg
        className={className}
        ref={ref}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="#10141B"
        xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7" stroke="var(--text-icons-gray-200)" strokeWidth="1" />
    </svg>
));
// #endregion
